import React, { useEffect, useRef } from 'react';

import { useAtom, useSetAtom } from 'jotai';
import { DateValue } from 'react-aria-components';
import {
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import {
  uiRoutes,
  useAccountInfo,
  useBillingPeriods,
  useFullPayoffAmount,
  useLoanSummary,
  usePaymentCalendarDates,
  usePaymentMessaging,
  useScheduledPayments,
  useTracking,
} from '@consumer-frontend/nc-api';
import {
  defaultSetValueOpts,
  isDefined,
  isLineOfCredit,
  LOAN_STATUS,
  NcMakeAPaymentFormFieldsT,
  PaymentMethodT,
  redirectTo,
  today,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import {
  accountIdAtom,
  hasFetchErrorAtom,
} from '../../store';
import { NcAlert } from '../NcAlert';
import { NcButton } from '../NcButton';
import { NcFieldset } from '../NcFieldset';
import { NcForm } from '../NcForm';
import { NcLoadingSpinner } from '../NcLoadingSpinner';
import { NcPaymentAmount } from '../NcPaymentAmount';
import { NcPaymentDate } from '../NcPaymentDate';
import { NcPaymentMethod } from '../NcPaymentMethod';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/payment/map-form.scss';

export type NcMakeAPaymentFormPropsT = NcMakeAPaymentFormFieldsT & {
  alertMessage?: React.ReactNode;
  isLoading?: boolean;
  onSubmit?: (data: NcMakeAPaymentFormFieldsT) => Promise<void>;
};
export type NcMakeAPaymentFormFieldNameT = keyof NcMakeAPaymentFormFieldsT;
export type NcMakeAPaymentFormT = React.FC<NcMakeAPaymentFormPropsT>;

export const NcMakeAPaymentForm: NcMakeAPaymentFormT = ({
  alertMessage,
  isLoading,
  onSubmit,
  ...initialValues
}) => {
  // Initialize form
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<NcMakeAPaymentFormFieldsT>({ defaultValues: initialValues });

  const selectedPaymentAmount = watch('paymentAmount', undefined);
  const selectedPaymentDate = watch('paymentDate', undefined);
  const selectedPaymentMethod = watch('paymentMethod', undefined);

  // State and API hooks
  const [accountId] = useAtom(accountIdAtom);
  const { accountInfo: { loanNumber, loanType } = {} } = useAccountInfo(accountId);
  const setHasFetchError = useSetAtom(hasFetchErrorAtom);

  const { loanSummary: {
    loanStatus,
    nextPayment,
    pastDueBalance,
  } = {} } = useLoanSummary(accountId, loanNumber);
  const {
    aggregatedScheduledPayment,
    amountDue,
    dueDate,
    originalAmountDue,
  } = nextPayment || {};
  const {
    isAutopay,
    isMultiple,
    isPending,
    scheduledAmount = 0,
    targetDate: scheduledDate,
  } = aggregatedScheduledPayment || {};

  const { amount: fullPayoffAmount } = useFullPayoffAmount({
    accountId,
    loanNumber,
    paymentDate: selectedPaymentDate,
  });

  const {
    error: hasPaymentCalendarDatesError,
    paymentCalendarDates: {
      invalidDates,
      minDate: minDateFromApi,
    } = {},
  } = usePaymentCalendarDates(
    accountId,
    loanNumber,
    selectedPaymentMethod || 'debit_card',
  );
  const { isLoading: areScheduledPaymentsLoading } = useScheduledPayments({ accountId, loanNumber });

  // Helpers
  const isCalledDue = loanStatus === LOAN_STATUS.CALLED_DUE;
  const isCurrent = loanStatus === LOAN_STATUS.CURRENT;

  // Conditionally adding loanNumber for LOCs only to not make request for
  // Installment loans as they dont have Billing Periods
  const {
    isLoading: areBillingPeriodsLoading,
    error: hasBillingPeriodsError,
    currentBillingPeriod,
  } = useBillingPeriods({
    accountId,
    loanNumber: isLineOfCredit(loanType) ? loanNumber : undefined,
  });

  const isPaymentDateAfterDueDate = !!(
    selectedPaymentDate && dueDate && selectedPaymentDate.compare(dueDate) > 0
  );
  const isPaymentDateOnOrAfterDueDate = !!(
    selectedPaymentDate && dueDate && selectedPaymentDate.compare(dueDate) >= 0
  );

  const isPaymentDateBeyondBillingPeriodEndDate = !!(
    selectedPaymentDate
    && currentBillingPeriod?.endDate
    && selectedPaymentDate.compare(currentBillingPeriod.endDate) > 0
  );

  const FULL_PAYOFF_SIMULATION_CUTOFF = 11;

  // The full payoff amount can be accurately simulated for the next billing period
  // but not for billing periods after. The shortest billing period possible is
  // 12 days, so don't enable the full payoff option when the user selects a date
  // more than 11 days after the end of the current billing period.
  const isPaymentDateBeyondFullPayoffSimulationDate = !!(
    selectedPaymentDate
    && currentBillingPeriod?.endDate
    && selectedPaymentDate.compare(currentBillingPeriod.endDate) > FULL_PAYOFF_SIMULATION_CUTOFF
  );

  const isCurrentBillingPeriodBillable = !!currentBillingPeriod?.isBillable;

  const isCurrentBillingPeriodDueDateBeforeNextDueDate = !!(
    dueDate
    && currentBillingPeriod?.dueDate
    && currentBillingPeriod.dueDate.compare(dueDate) < 0
  );

  const isPostBillingPeriodPaymentDate = isLineOfCredit(loanType)
    && isCurrent
    && isPaymentDateBeyondBillingPeriodEndDate
    && isCurrentBillingPeriodBillable
    && isCurrentBillingPeriodDueDateBeforeNextDueDate;

  const isPostDueDatePaymentDate = isLineOfCredit(loanType)
    && isCurrent
    && !!isAutopay
    && isPaymentDateAfterDueDate;

  const disableNextMinimumOptionLoc = isPostBillingPeriodPaymentDate || isPostDueDatePaymentDate;

  const disableNextMinimumOptionInstallment = !isLineOfCredit(loanType)
    && isCurrent
    && !!isAutopay
    && isPaymentDateOnOrAfterDueDate;

  const disableNextMinimumOption = disableNextMinimumOptionInstallment || disableNextMinimumOptionLoc;
  const disableFullPayoffOption = isLineOfCredit(loanType) && !isCalledDue && isPaymentDateBeyondFullPayoffSimulationDate;

  const hasFetchError = hasPaymentCalendarDatesError || hasBillingPeriodsError;

  const paymentAmountMessages = usePaymentMessaging({
    dueAmount: amountDue,
    dueDate: dueDate || today(),
    fullPayoffAmount,
    isAutopay: isAutopay || false,
    isAutopayPending: isPending || false,
    isFullPayoffDisabled: disableFullPayoffOption,
    isMultiple: isMultiple || false,
    isNextMinimumPaymentDisabled: disableNextMinimumOption,
    loanStatus,
    originalDueAmount: originalAmountDue,
    paymentAmount: selectedPaymentAmount,
    paymentDate: selectedPaymentDate,
    productType: loanType,
    scheduledAmount,
    scheduledDate,
  });

  const { trackEvent } = useTracking(accountId);
  const hasFiredTrackingEvent = useRef(false);

  // Event Handlers and Validations
  const onMethodChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setValue('paymentMethod', ev.target.value as PaymentMethodT, defaultSetValueOpts);
  };

  const onDateChange = (date: DateValue | null) => {
    setValue('paymentDate', date || undefined, defaultSetValueOpts);

    if (selectedPaymentAmount === fullPayoffAmount) {
      setValue('paymentAmount', undefined, { ...defaultSetValueOpts, shouldValidate: false });
    }
  };

  const updatePaymentAmount = (amount: number | undefined) => {
    setValue('paymentAmount', amount, defaultSetValueOpts);
  };

  // Validations
  const paymentAmountValidations = {
    required: 'Select a payment amount.',
    validate: (value?: number) => {
      if (value === undefined) { return 'Select a payment amount.'; }
      if (value <= 0) { return 'Payment amount must be greater than $0.00.'; }
      if (Math.round(value % 0.01) !== 0) { return `${value} is an invalid amount.`; }

      if (isDefined(fullPayoffAmount) && value > fullPayoffAmount) {
        return 'Payment amount cannot exceed full payoff amount.';
      }

      return true;
    },
  };

  const paymentDateValidations = {
    validate: (value?: DateValue) => {
      if (!value) { return 'Select a payment date.'; }

      const maxDate = today().add({ days: 35 });
      const minDate = minDateFromApi || today();

      if (minDate.compare(value) > 0) { return `Payment date cannot be before ${minDate.toString()}.`; }
      if (maxDate.compare(value) < 0) { return `Payment date cannot be after ${maxDate.toString()}.`; }

      if (invalidDates?.some((invalidDate) => invalidDate.compare(value) === 0)) {
        return 'Payment cannot be made on this date. Enter or select an available date from the calendar.';
      }

      return true;
    },
  };

  // Submit handlers
  const onValidSubmit: SubmitHandler<NcMakeAPaymentFormFieldsT> = async (data) => {
    await onSubmit?.({
      ...data,
      fullPayoffAmount,
      paymentMessaging: paymentAmountMessages,
    });
  };

  const onInvalidSubmit: SubmitErrorHandler<NcMakeAPaymentFormFieldsT> = async (validationFailures) => {
    console.warn('INVALID', validationFailures);
  };

  const onCancel = () => {
    reset({
      paymentAmount: undefined,
      paymentDate: undefined,
      paymentMethod: undefined,
    });

    // Use navigate once account summary has been redesigned
    redirectTo(uiRoutes.legacyAH.accountSummary);
  };

  // Effects
  // Clear next installment selection when it becomes disabled
  useEffect(
    () => {
      if (disableNextMinimumOption && selectedPaymentAmount === amountDue) {
        setValue('paymentAmount', undefined, { ...defaultSetValueOpts, shouldValidate: false });
      }
    },
    // NOTE: temporarily disabled linter. To be updated in NCFE-10030. This prevents custom amount from
    // being cleared when disableNextMinimumOption is true but the custom amount is selected
    // with the same value as the next minimum amount due.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableNextMinimumOption, setValue],
  );

  // Update global error state if API error occurs
  useEffect(
    () => { setHasFetchError((prev) => prev || Boolean(hasFetchError)); },
    [hasFetchError, setHasFetchError],
  );

  // Fire form-loaded tracking event.
  useEffect(
    () => {
      // This should only run once--after all data has loaded.
      if (isLoading
        || areBillingPeriodsLoading
        || areScheduledPaymentsLoading
        || hasFiredTrackingEvent.current
      ) { return; }

      hasFiredTrackingEvent.current = true;

      try {
        trackEvent({
          event_data: {
            autopay_amount: scheduledAmount,
            autopay_target_date: scheduledDate?.toString(),
            is_autopay_pending: isAutopay && isPending,
            is_enrolled_in_autopay: isAutopay,
            loan_number: loanNumber,
            loan_status: loanStatus,
            multiple_autopay: isMultiple,
            next_amount_due: amountDue,
            next_due_date: isDefined(dueDate) ? dueDate.toString() : '',
            num_days_until_next_due_date: isDefined(dueDate) ? dueDate.compare(today()) : undefined,
            past_due_balance: pastDueBalance,
            product: loanType,
          },
          event_description: 'paymentFormLoaded',
        });
      } catch {
        /* Swallow tracking errors */
        hasFiredTrackingEvent.current = false;
      }
    },
    [
      amountDue,
      areBillingPeriodsLoading,
      areScheduledPaymentsLoading,
      dueDate,
      isAutopay,
      isLoading,
      isMultiple,
      isPending,
      loanNumber,
      loanStatus,
      loanType,
      pastDueBalance,
      scheduledAmount,
      scheduledDate,
      trackEvent,
    ],
  );

  return (
    <Container className="nc-make-a-payment-form-container" hasBoxShadow>
      {alertMessage && (
        <>
          <NcAlert
            chromaticIgnore
            dismissible={false}
            variant="danger"
          >
            {alertMessage}
          </NcAlert>
          {!pastDueBalance && <hr />}
        </>
      )}

      <h1 className="h2">Make a Payment</h1>

      {isLoading && <NcLoadingSpinner />}

      {!isLoading && (
        <NcForm className="nc-make-a-payment-form" onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}>
          <NcPaymentMethod
            {...register('paymentMethod', { required: 'Select a payment method.' })}
            errorMessage={errors.paymentMethod?.message as string}
            onChange={onMethodChange}
            value={selectedPaymentMethod}
          />

          <NcPaymentDate
            {...register('paymentDate', paymentDateValidations)}
            errorMessage={errors.paymentDate?.message as string}
            onChange={onDateChange}
            paymentMethod={selectedPaymentMethod}
            value={selectedPaymentDate}
          />

          <NcPaymentAmount
            {...register('paymentAmount', paymentAmountValidations)}
            disableFullPayoffOption={disableFullPayoffOption}
            disableNextMinimumOption={disableNextMinimumOption}
            errorMessage={errors.paymentAmount?.message as string}
            infoMessages={paymentAmountMessages}
            paymentDate={selectedPaymentDate}
            updateValue={updatePaymentAmount}
            value={selectedPaymentAmount}
          />

          {/* CTA Buttons */}
          <NcFieldset className="cta-buttons">
            <NcButton
              type="submit"
              loading={isLoading || isSubmitting}
            >
              Continue to Review
            </NcButton>

            <NcButton
              variant="outline-primary"
              onClick={onCancel}
              disabled={isLoading || isSubmitting}
            >
              Cancel
            </NcButton>
          </NcFieldset>
        </NcForm>
      )}
    </Container>
  );
};
